import React, { useContext, useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { LanguageContext } from '../contexts/LanguageContext';
import { getHeadingFontClass } from '../utils/fontUtils';
import SectionLayout from '../components/SectionLayout';
import Button from '../components/ui/Button';
import Label from '../components/ui/Label';
import { getServiceUrl } from '../config/service';

// 定数の分離
const FADE_DELAY = 50;
const FADE_DURATION = 700;
const ANIMATION_DELAY_STEP = 0.2;

// 外部リンクの定数
const EXTERNAL_LINKS = {
  X_JPN_TCG: 'https://twitter.com/jpn_tcg_center',
};

// 画像パスの定数
const IMAGE_PATHS = {
  JP_APP: `${process.env.PUBLIC_URL}/assets/images/img_service_jp_app.png`,
};

// Xアイコンのコンポーネント（SVGを使用）
const XIcon = ({ className }) => (
  <svg
    className={className}
    fill="currentColor"
    viewBox="0 0 24 24"
    aria-hidden="true"
  >
    <path d="M18.901 1.153h3.68l-8.04 9.19L24 22.846h-7.406l-5.8-7.584-6.638 7.584H.474l8.6-9.83L0 1.154h7.594l5.243 6.932L18.901 1.153ZM17.61 20.644h2.039L6.486 3.24H4.298L17.61 20.644Z" />
  </svg>
);

// 画像切り替えフック
const useImageSwitcher = (imageSrc, imageSrcJa, locale) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [currentSrc, setCurrentSrc] = useState(imageSrc);

  const targetImageSrc = locale === 'ja' && imageSrcJa ? imageSrcJa : imageSrc;

  useEffect(() => {
    if (targetImageSrc === currentSrc) {
      if (!imageLoaded) {
        setImageLoaded(true);
      }
      return;
    }

    setImageLoaded(false);

    const img = new Image();
    img.onload = () => {
      setCurrentSrc(targetImageSrc);
      setTimeout(() => setImageLoaded(true), FADE_DELAY);
    };
    img.src = targetImageSrc;
  }, [targetImageSrc, currentSrc, imageLoaded]);

  return { currentSrc, imageLoaded };
};

// 各サービスカードのコンポーネント
const ServiceCard = ({
  titleId,
  descriptionId,
  xLink,
  siteLink,
  imageSrc,
  imageSrcJa,
  imageAltId,
  index,
  label,
  isLast,
}) => {
  const intl = useIntl();
  const { locale } = useContext(LanguageContext);
  const { currentSrc, imageLoaded } = useImageSwitcher(
    imageSrc,
    imageSrcJa,
    locale
  );

  const animationDelay = `${index * ANIMATION_DELAY_STEP}s`;

  return (
    <div
      className={`flex flex-col lg:flex-row items-start justify-between pt-12 ${
        isLast ? '' : 'pb-12 border-b border-gray-200'
      } animate-fadeInUp`}
      style={{ animationDelay }}
    >
      {/* サービス説明部分 */}
      <div className="w-full lg:w-1/2 lg:pr-8 order-2 lg:order-1">
        <div className="flex flex-row items-center mb-4">
          <h3
            className={`text-xl sm:text-2xl font-bold text-foreground ${getHeadingFontClass(
              locale
            )} mr-2 flex items-center`}
          >
            {intl.formatMessage({ id: titleId })}
          </h3>
          {label && (
            <div className="flex items-center transform translate-y-0 md:translate-y-0.5">
              <Label type={label.type} isVisible={label.isVisible} />
            </div>
          )}
        </div>
        <p className="text-muted-foreground leading-loose mb-6 font-body">
          {intl.formatMessage({ id: descriptionId })}
        </p>
        {/* ボタン部分 */}
        <div className="flex flex-col sm:flex-row gap-4">
          {siteLink && (
            <Button
              href={siteLink}
              variant="default"
              size="lg"
              className="w-full sm:w-auto"
            >
              {intl.formatMessage({ id: 'service.button.site' })}
            </Button>
          )}
          {xLink && (
            <Button
              href={xLink}
              variant="xlink"
              size="lg"
              className="w-full sm:w-auto"
            >
              <XIcon className="w-5 h-5 mr-2" />
              {intl.formatMessage({ id: 'service.button.x' })}
            </Button>
          )}
        </div>
      </div>
      {/* サービス画像部分 */}
      <div className="w-full lg:w-1/2 mb-6 lg:mb-0 lg:mt-0 order-1 lg:order-2">
        <div className="aspect-[3/2] w-full rounded-lg overflow-hidden">
          <div className="w-full h-full overflow-hidden">
            <img
              src={currentSrc}
              alt={intl.formatMessage({ id: imageAltId })}
              className={`w-full h-full object-cover sm:transition-transform sm:duration-500 sm:ease-in-out sm:hover:scale-105 transition-opacity ease-in-out ${
                imageLoaded ? 'opacity-100' : 'opacity-0'
              }`}
              style={{ transitionDuration: `${FADE_DURATION}ms` }}
              onLoad={() => {
                // 画像が読み込まれた時の処理は useImageSwitcher で管理
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

// メインのServiceコンポーネント
export default function Service() {
  const intl = useIntl();

  // サービス情報の配列
  const services = [
    {
      titleId: 'service.card1.title',
      descriptionId: 'service.card1.description',
      siteLink: getServiceUrl(intl.locale),
      xLink: EXTERNAL_LINKS.X_JPN_TCG,
      imageSrc: IMAGE_PATHS.JP_APP,
      imageSrcJa: IMAGE_PATHS.JP_APP,
      imageAltId: 'service.card1.imageAlt',
    },
  ];

  return (
    <SectionLayout accent="none" background="gray">
      <h2
        className="section-title animate-slideInRight"
        style={{ animationDelay: '0.3s' }}
      >
        {intl.formatMessage({ id: 'service.title' })}
      </h2>
      <p
        className="section-subtitle animate-slideInRight"
        style={{ animationDelay: '0.5s' }}
      >
        {intl.formatMessage({ id: 'service.subtitle' })}
      </p>
      {/* サービスカードのマッピング */}
      <div>
        {services.map((service, index) => (
          <ServiceCard
            key={index}
            {...service}
            index={index}
            isLast={index === services.length - 1}
          />
        ))}
      </div>
    </SectionLayout>
  );
}
